import { Suspense, useState } from 'react';

import { TRecursiveNonNullable } from '@api/Helper.types';
import {
    fetchPatchApplicationsAdditionalInfoRequest,
    GetApplicationsDocumentReviewFeedbackResponse
} from '@iwoca/lapi-client/edge';
import { AlertBox } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { ExclamationMarkOutlineSVG } from '@svgComponents/exclamationMarkOutlineSVG/ExclamationMarkOutlineSVG';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { MarkDoneButton } from './MarkDoneButton';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../pageStructure/mainContentPending';
import styles from '../TradingData.module.css';
import { useGetTradingHistoryData } from '../useGetTradingHistoryData';

type TFeedbackRequestType =
    TRecursiveNonNullable<GetApplicationsDocumentReviewFeedbackResponse>['data']['additional_info_requests'][number]['request_type'];

export const DocumentFeedback = () => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testid="document-feedback-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div className="col-span-2 row-span-2 max-sm:order-last max-sm:col-span-full">
                    <DocumentFeedbackContent />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const DocumentFeedbackContent = () => {
    const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);
    const { documentFeedback, applicationId } = useGetTradingHistoryData();

    const hasAdditionalInfo =
        documentFeedback?.additional_info_requests &&
        documentFeedback.additional_info_requests.length > 0;

    const additionalInformation = documentFeedback?.additional_info_requests;
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const intl = useIntl();

    const markedAsDone =
        !!additionalInformation &&
        additionalInformation.every((info) => info.marked_as_done_by_customer);

    const onMarkAsDone = () => {
        setIsUpdatingFeedback(true);
        markDocumentsAsDone();
    };

    const { mutate: markDocumentsAsDone } = useMutation({
        mutationFn: async () => {
            if (!additionalInformation) {
                throw Error('No additional info to mark as done');
            }

            await Promise.all(
                additionalInformation.map((info) =>
                    fetchPatchApplicationsAdditionalInfoRequest({
                        requestId: info.id.toString(),
                        applicationId: applicationId!,
                        body: {
                            marked_as_done_by_customer: !markedAsDone
                        }
                    })
                )
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                'document_review_feedback',
                stateKey,
                applicationId
            ]);
            setIsUpdatingFeedback(false);
        }
    });

    const renderAdditionalRequests = () => {
        if (!additionalInformation || additionalInformation.length === 0) {
            return null;
        }

        const supportEmailText = intl.formatMessage({
            id: '_.trading_data.document_feedback.support_email',
            defaultMessage: 'support@iwoca.co.uk'
        });

        const INFO_REQUEST_TEXTS: Record<
            TFeedbackRequestType,
            React.ReactNode
        > = {
            ADG: (
                <>
                    <FormattedMessage
                        id="_.trading_data.document_feedback.adg_required_initial"
                        defaultMessage="Additional Guarantor: We need approval and credit verification from the other managing directors. Please follow these steps to add them:"
                    />

                    <ol className="ml-xl list-decimal">
                        <li>
                            <FormattedMessage
                                id="_.trading_data.document_feedback.adg_required_step_1"
                                defaultMessage="Go to Menu"
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="_.trading_data.document_feedback.adg_required_step_2"
                                defaultMessage="Select Profile"
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="_.trading_data.document_feedback.adg_required_step_3"
                                defaultMessage="Click 'Add another person'"
                            />
                        </li>
                    </ol>
                    <FormattedMessage
                        id="_.trading_data.document_feedback.adg_required_final"
                        defaultMessage="You’ll be asked to enter their details so we can complete the required checks. Once all directors are added and verified, we’ll be able to move forward with your application."
                    />
                </>
            ),
            LOAN_PURPOSE: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.loan_purpose_required"
                    defaultMessage="Loan purpose: Please may you email us a brief explanation of the purpose of the loan to support@iwoca.co.uk."
                />
            ),
            DATE_OF_FOUNDATION: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.date_of_foundation_required"
                    defaultMessage="Date of foundation: Please may you email us confirmation of your company’s date of foundation to support@iwoca.co.uk."
                />
            ),
            TRADE_LICENSE: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.trade_license_required"
                    defaultMessage="Trade license: Please may you send us a copy of your business registration to support@iwoca.co.uk."
                />
            ),
            LEGAL_FORM: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.legal_form_required"
                    defaultMessage="Clarify legal form: Please may you send us a copy of your business registration to support@iwoca.co.uk. If you do not need a business registration for your business, please let us know via email what your business specialises in and how long it has been operating."
                />
            ),
            NO_ACCOUNT_NUMBER: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.account_number_required"
                    defaultMessage="Bank statements: Please provide us with business bank statements from the past 90 days. Please upload these statements in PDF format, ensuring they include balance figures and all transactions. We require 90 days of statements from all accounts that represent more than 10% of your total business revenue. Alternatively, you can transfer these to us directly via Open Banking."
                />
            ),
            CHECK_OPTIONAL_ADG: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.optional_adg_check"
                    defaultMessage="Optional additional guarantor: You have the option to add additional directors as guarantors - you can add these directly in your profile under Menu – Profile by clicking on ‘Add another person’ and filling in the necessary details about additional managing directors."
                />
            ),
            RE_LINK_OB: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.open_banking_required"
                    defaultMessage="Open Banking: Please link your business bank account again via Open Banking to provide us with business bank statements from the past 90 days. We require 90 days of statements from all accounts that represent more than 10% of your total business revenue. Alternatively you can upload these statements in PDF format."
                />
            ),
            OB_LINK_ERROR: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.open_banking_error"
                    defaultMessage="Open Banking: Unfortunately, we were not able to access your business account via Open Banking. Please try linking your account again, or alternatively you can upload bank statements from the past 90 days from all accounts that represent more than 10% of your total business revenue. Please ensure they are in PDF format and include balance figures and all transactions."
                />
            ),
            TURNOVER_CLARIFICATION: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.turnover_clarification"
                    defaultMessage="Turnover clarification: The turnover on the bank statements submitted differ from the information you provided in your application. Please may you send us a brief explanation to {supportLink}. If you use any other accounts, please also upload the current statements for these below."
                    values={{
                        supportLink: (
                            <a
                                className={styles.supportLink}
                                href={`mailto:${supportEmailText}`}
                            >
                                {supportEmailText}
                            </a>
                        )
                    }}
                />
            ),
            PERSONAL_ID: (
                <FormattedMessage
                    id="_.trading_data.document_feedback.personal_id"
                    defaultMessage="Copy of ID: We need to verify your identity. Please may you upload a copy of both sides of your identity card."
                />
            )
        };

        return (
            <ul className="mx-auto list-disc pl-xl text-left font-reg">
                {additionalInformation
                    .filter(
                        (request) => request.request_type in INFO_REQUEST_TEXTS
                    )
                    .map((request) => (
                        <li
                            key={request.request_type}
                            className="mb-s"
                            data-testid="additional-info-request"
                        >
                            {INFO_REQUEST_TEXTS[request.request_type]}
                        </li>
                    ))}
            </ul>
        );
    };

    return (
        <>
            <AlertBox
                variant="failure"
                className="mb-xl flex flex-col gap-m border-2 !bg-transparent p-l"
            >
                <Text className="font-bold">
                    <FormattedMessage
                        id="_.trading_data.document_feedback.title"
                        defaultMessage="Thank you for making an application with us, we need a little more information from you so that we can move forward to a decision."
                    />
                </Text>
                <Text>
                    <FormattedMessage
                        id="_.trading_data.document_feedback.subtitle"
                        defaultMessage="Please review the feedback below marked with a warning icon and share any required documents or information. Click ‘Mark as done’ when you’ve completed it and our team will review the information within 24 hours."
                    />
                </Text>
            </AlertBox>
            {hasAdditionalInfo && (
                <AlertBox
                    variant="failure"
                    className="mb-3xl flex flex-col gap-s !bg-structure-100 px-xl py-l"
                >
                    <Text className="font-bold">
                        <FormattedMessage
                            id="_.trading_data.document_feedback.additional_information"
                            defaultMessage="Additional Information Required"
                        />
                    </Text>
                    <div className="flex flex-col justify-between gap-m min-sm:flex-row">
                        <div
                            className={classNames({
                                'opacity-50': markedAsDone
                            })}
                        >
                            <div className="flex items-center gap-s py-s">
                                <ExclamationMarkOutlineSVG className="min-h-l min-w-l" />
                                <Text className="m-0 font-med text-error">
                                    <FormattedMessage
                                        id="_.trading_data.document_feedback.additional_information_warning"
                                        defaultMessage="We need to confirm some additional information"
                                    />
                                </Text>
                            </div>
                            {renderAdditionalRequests()}
                        </div>
                        <MarkDoneButton
                            isMarkedAsDone={markedAsDone}
                            onClick={onMarkAsDone}
                            isLoading={isUpdatingFeedback}
                            feedbackCategory="additional_info_requests"
                        />
                    </div>
                </AlertBox>
            )}
        </>
    );
};
